'use client';

import { useState, useEffect, useRef } from 'react';
import { useRouter } from 'next/navigation';
import Link from 'next/link';
import { createClient } from '@/lib/supabase/client';
import { capture, getPostHogClient } from '@/lib/analytics/posthog';
import { VerbalLogo } from '@/components/verbal-logo';
import { AlertCircle } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { AuthTwoPane } from '@/components/auth/AuthTwoPane';
import { LoginEditorial } from '@/components/auth/editorial/LoginEditorial';

import { Button } from '@/components/ui/button';

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

const DEV_EMAIL = 'test@getverbal.ai';
const DEV_PASSWORD = 'password';

function isLocalDev(): boolean {
  const url = process.env.NEXT_PUBLIC_SUPABASE_URL ?? '';
  return url.includes('127.0.0.1') || url.includes('localhost');
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export default function LoginPage() {
  const router = useRouter();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [devAutoLogin, setDevAutoLogin] = useState(false);
  const autoLoginAttempted = useRef(false);

  // Auto-sign-in for local dev
  useEffect(() => {
    if (!isLocalDev() || autoLoginAttempted.current) return;
    autoLoginAttempted.current = true;
    setDevAutoLogin(true);

    const supabase = createClient();
    supabase.auth
      .signInWithPassword({ email: DEV_EMAIL, password: DEV_PASSWORD })
      .then(({ error: authError }) => {
        if (authError) {
          setDevAutoLogin(false);
          return;
        }
        router.push('/dashboard');
        router.refresh();
      })
      .catch(() => {
        setDevAutoLogin(false);
      });
  }, [router]);

  async function handleGoogleLogin() {
    setSubmitting(true);
    setError(null);
    capture('login_attempted', { method: 'google' });

    try {
      const supabase = createClient();

      const { error: authError } = await supabase.auth.signInWithOAuth({
        provider: 'google',
        options: {
          redirectTo: `${window.location.origin}/auth/callback`,
        },
      });

      if (authError) {
        throw new Error(authError.message);
      }
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Google sign in failed. Please try again.';
      setError(message);
      setSubmitting(false);
    }
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();

    if (!email.trim()) {
      setError('Email is required.');
      return;
    }

    if (!password) {
      setError('Password is required.');
      return;
    }

    setSubmitting(true);
    setError(null);
    capture('login_attempted', { method: 'email' });

    try {
      const supabase = createClient();

      const { data, error: authError } = await supabase.auth.signInWithPassword({
        email: email.trim(),
        password,
      });

      if (authError) {
        throw new Error(authError.message);
      }

      capture('login_succeeded', { method: 'email' });
      if (data.user) {
        getPostHogClient()?.identify(data.user.id, {
          email: data.user.email,
        });
      }

      router.push('/dashboard');
      router.refresh();
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Sign in failed. Please try again.';
      capture('login_failed', { method: 'email', error_message: message });
      setError(message);
    } finally {
      setSubmitting(false);
    }
  }

  if (devAutoLogin) {
    return (
      <main className="flex min-h-dvh items-center justify-center bg-paper px-4">
        <div className="text-center">
          <VerbalLogo className="mx-auto h-10" />
          <p className="mt-4 text-sm text-graphite">
            Signing in as {DEV_EMAIL}&hellip;
          </p>
        </div>
      </main>
    );
  }

  return (
    <AuthTwoPane editorial={<LoginEditorial />}>
        <h1 className="mb-6 text-2xl font-semibold uppercase tracking-tight text-ink">
          Sign in to Verbal
        </h1>

        {/* Form card */}
        <div className="rounded-sm border border-rule bg-bg-elevated p-8">
          <form onSubmit={handleSubmit} className="space-y-5">
            {error && (
              <div role="alert" className="bg-vellum border-l-[3px] border-l-burn rounded-sm p-4 text-sm text-ink flex items-start gap-2">
                <AlertCircle aria-hidden="true" className="mt-0.5 h-4 w-4 shrink-0 text-burn" />
                <span>{error}</span>
              </div>
            )}
            <div>
              <label htmlFor="email-v2" className="block text-[11px] font-medium uppercase tracking-[0.1em] text-graphite">
                Email address
              </label>
              <Input
                id="email-v2"
                name="email"
                type="email"
                value={email}
                onChange={(e) => { setEmail(e.target.value); setError(null); }}
                autoComplete="email"
                spellCheck={false}
                placeholder="you@company.com"
                className="mt-1 w-full"
              />
            </div>
            <div>
              <label htmlFor="password-v2" className="block text-[11px] font-medium uppercase tracking-[0.1em] text-graphite">
                Password
              </label>
              <Input
                id="password-v2"
                name="password"
                type="password"
                value={password}
                onChange={(e) => { setPassword(e.target.value); setError(null); }}
                autoComplete="current-password"
                placeholder="Enter your password"
                className="mt-1 w-full"
              />
            </div>
            <Button type="submit" disabled={submitting} className="w-full">
              {submitting ? 'Signing in…' : 'Sign in'}
            </Button>
          </form>
          <div className="mt-6 flex items-center gap-3">
            <div className="h-0.5 flex-1 bg-vellum" />
            <span aria-hidden="true" className="text-[11px] font-medium uppercase tracking-[0.1em] text-graphite">or</span>
            <div className="h-0.5 flex-1 bg-vellum" />
          </div>
          <Button type="button" variant="outline" disabled={submitting} onClick={handleGoogleLogin} className="mt-4 flex w-full items-center justify-center gap-2">
            <svg className="h-4 w-4" viewBox="0 0 24 24" aria-hidden="true">
              <path fill="currentColor" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" />
              <path fill="currentColor" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
              <path fill="currentColor" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" />
              <path fill="currentColor" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" />
            </svg>
            Continue with Google
          </Button>
          <div className="mt-6 border-t border-rule pt-4 text-center">
            <p className="text-sm text-graphite">
              Don&apos;t have an account?{' '}
              <Link href="/signup" className="font-bold text-salmon-text hover:underline">Sign up</Link>
            </p>
          </div>
        </div>
      </AuthTwoPane>
  );
}
